import { useAuth } from '../hooks/useAuth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Typography,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useStore, Integrador } from '../store';
import api, { integradoresAPI } from '../api';
import PlanChip from '../components/PlanChip';
import ResourceList, { type ColumnDefinition } from '../components/shared/ResourceList';

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export default function Integradores() {
  const { can } = useAuth();
  const { integradores, setIntegradores, loading } = useStore();
  const PROTECTED_ALIAS = 'jbesolar';
  const TENANT_DOMAIN = 'postsales.jbesolucoes.com.br';
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingIntegrador, setEditingIntegrador] = useState<Integrador | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Integrador | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    empresa: '',
    plano: 'free',
    alias: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [catalog, setCatalog] = useState<{
    plans: { code: string; name: string; published: boolean }[];
    settings?: { trialEnabled: boolean; trialPlan: string; trialDays: number };
  } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [planCode, setPlanCode] = useState('');
  const [initialStatus, setInitialStatus] = useState<'active' | 'trial'>('active');
  useEffect(() => {
    if (!openDialog || editingIntegrador) return;
    let alive = true;
    setCatalog(null);
    setPlanCode('');
    setInitialStatus('active');
    setCatalogLoading(true);
    setCatalogError('');
    api
      .get('/commercial/catalog')
      .then(({ data }) => {
        if (alive) setCatalog(data);
      })
      .catch(() => {
        if (alive) setCatalogError('Não foi possível consultar os planos. Tente novamente.');
      })
      .finally(() => {
        if (alive) setCatalogLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [openDialog, editingIntegrador]);
  const publishedPlans = catalog?.plans.filter((plan) => plan.published) || [];
  const trialAvailable =
    !!catalog?.settings?.trialEnabled &&
    publishedPlans.some((plan) => plan.code === catalog.settings?.trialPlan);
  const subscriptionReady =
    !catalogLoading &&
    publishedPlans.some((plan) => plan.code === planCode) &&
    (initialStatus === 'active' || (trialAvailable && planCode === catalog?.settings?.trialPlan));

  // Gera slug a partir do nome da empresa
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
      .trim()
      .replace(/\s+/g, '-') // Substitui espaços por hífen
      .replace(/-+/g, '-') // Remove hífens duplicados
      .substring(0, 30); // Limita tamanho
  };

  const handleOpenDialog = () => {
    setEditingIntegrador(null);
    setFormData({ nome: '', email: '', empresa: '', plano: 'free', alias: '' });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (integrador: Integrador) => {
    setEditingIntegrador(integrador);
    setFormData({
      nome: integrador.nome,
      email: integrador.email,
      empresa: integrador.empresa,
      plano: integrador.plano,
      alias: integrador.alias,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    setOpenDialog(false);
    setEditingIntegrador(null);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    // Auto-gera alias quando empresa é alterada
    if (name === 'empresa') {
      const slug = generateSlug(value);
      setFormData((prev) => ({ ...prev, empresa: value, alias: slug }));
    } else if (name === 'alias') {
      // Valida e limpa o alias manualmente digitado
      const cleanAlias = value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .substring(0, 30);
      setFormData((prev) => ({ ...prev, [name]: cleanAlias }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (submitting || (!editingIntegrador && !subscriptionReady)) return;
    setSubmitting(true);
    try {
      if (editingIntegrador) {
        // Edição — envia apenas campos editáveis (alias não muda)
        const { nome, email, empresa, plano } = formData;
        const response = await integradoresAPI.atualizar(editingIntegrador._id, {
          nome,
          email,
          empresa,
          plano: plano as 'free' | 'pro' | 'enterprise',
        });
        setIntegradores(
          integradores.map((int) => (int._id === editingIntegrador._id ? { ...int, ...response.data } : int))
        );
        setOpenDialog(false);
        setEditingIntegrador(null);
      } else {
        // Criação
        const payload = {
          nome: formData.nome,
          email: formData.email,
          empresa: formData.empresa,
          alias: formData.alias,
          subscription: {
            revision: 0,
            planCode,
            status: initialStatus,
            endsAt: null,
            reason: 'Assinatura inicial no cadastro do integrador',
          },
        };
        const response = await integradoresAPI.criar(payload);
        setIntegradores([...integradores, response.data.integrador]);
        setOpenDialog(false);
        setEditingIntegrador(null);
        alert(response.data.message);
      }
    } catch (error: any) {
      const refreshed = await integradoresAPI.listar().catch(() => null);
      if (refreshed) setIntegradores(refreshed.data.data);
      alert(
        error.response?.data?.error ||
          (editingIntegrador ? 'Erro ao atualizar integrador' : 'Erro ao criar integrador')
      );
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (integrador: Integrador) => {
    setDeleteTarget(integrador);
    setDeleteConfirmation('');
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
    setDeleteConfirmation('');
  };

  const isProtectedTarget = !!deleteTarget && deleteTarget.alias.toLowerCase() === PROTECTED_ALIAS;

  const canConfirmDelete =
    !!deleteTarget && !isProtectedTarget && deleteConfirmation.trim() === deleteTarget.alias;

  const handleDelete = async () => {
    if (!deleteTarget || !canConfirmDelete) return;

    setDeleting(true);
    try {
      await integradoresAPI.deletar(deleteTarget._id);
      setIntegradores(integradores.filter((int) => int._id !== deleteTarget._id));
      handleCloseDeleteDialog();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Erro ao deletar integrador';
      alert(message);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'error' | 'warning'> = {
      ativo: 'success',
      inativo: 'error',
      suspenso: 'warning',
    };
    return colors[status] || 'default';
  };

  const handleCopyAlias = useCallback(async (alias: string) => {
    try {
      await navigator.clipboard.writeText(alias);
    } catch {
      // Ignore clipboard errors silently.
    }
  }, []);

  const integradoresFiltrados = useMemo(() => {
    return integradores
      .slice()
      .sort((a, b) => new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime());
  }, [integradores]);

  const fetchData = useCallback(
    async ({ page, limit, search }: { page: number; limit: number; search?: string }) => {
      const query = search ? normalizeText(search) : '';

      const filtered = integradoresFiltrados.filter((int) => {
        const dominio = `${int.alias}.postsales.jbesolucoes.com.br`;
        const haystack = normalizeText(
          [int.nome, int.empresa, int.email, int.alias, int.status, int.plano, dominio].join(' ')
        );

        return !query || haystack.includes(query);
      });

      const total = filtered.length;
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);

      return { total, data };
    },
    [integradoresFiltrados]
  );

  const columns = useMemo<ColumnDefinition<Integrador>[]>(() => {
    return [
      {
        header: 'Integrador',
        accessor: (int) => {
          const dominioExibicao = `${int.alias}.postsales.jbesolucoes.com.br`;
          const initials = int.nome
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() || '')
            .join('');

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <Avatar
                src={int.company_branding_logo_url || int.logo_url}
                alt={int.nome}
                sx={{ width: 38, height: 38, bgcolor: 'grey.200' }}
              >
                {initials || 'I'}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {int.nome}
                  </Typography>
                  <PlanChip plan={int.plano} commercialName={int.commercialPlan?.name} />
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {int.empresa}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  <Box
                    component="a"
                    href={`mailto:${int.email}`}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {int.email}
                  </Box>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.4, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="primary" sx={{ fontFamily: 'monospace' }}>
                    {int.alias}
                  </Typography>
                  <Tooltip title="Copiar alias">
                    <IconButton size="small" onClick={() => handleCopyAlias(int.alias)}>
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir tenant">
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://${dominioExibicao}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          );
        },
      },
      {
        header: 'Status',
        align: 'center',
        accessor: (int) => <Chip label={int.status} color={getStatusColor(int.status)} size="small" />,
      },
      {
        header: 'Ações',
        align: 'right',
        accessor: (int) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
            <Tooltip title="Editar integrador">
              <IconButton
                size="small"
                color="primary"
                disabled={!can('integrators.update')}
                onClick={() => handleOpenEditDialog(int)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deletar integrador">
              <IconButton
                size="small"
                color="error"
                disabled={!can('integrators.delete') || int.alias === PROTECTED_ALIAS}
                onClick={() => handleOpenDeleteDialog(int)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];
  }, [handleCopyAlias, can]);

  return (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <ResourceList
          resourceName="Integradores"
          searchPlaceholder="Buscar integradores..."
          emptyMessage="Nenhum integrador cadastrado"
          fetchData={fetchData}
          columns={columns}
          headerActions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              disabled={!can('integrators.create')}
              fullWidth
            >
              Novo Integrador
            </Button>
          }
          defaultRowsPerPage={10}
        />
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIntegrador ? 'Editar Integrador' : 'Novo Integrador'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '16px' }}>
          <TextField label="Nome" name="nome" value={formData.nome} onChange={handleInputChange} fullWidth />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            fullWidth
          />
          <TextField
            label="Empresa"
            name="empresa"
            value={formData.empresa}
            onChange={handleInputChange}
            fullWidth
            helperText="O alias será gerado automaticamente"
          />
          <TextField
            label="Alias (Subdomínio)"
            name="alias"
            value={formData.alias}
            onChange={handleInputChange}
            fullWidth
            disabled={!!editingIntegrador}
            helperText={
              <span>
                URL: <strong>{formData.alias || 'alias'}.postsales.jbesolucoes.com.br</strong>
                <br />
                {editingIntegrador
                  ? 'Alias não pode ser alterado'
                  : 'Apenas letras minúsculas, números e hífen'}
              </span>
            }
            placeholder="exemplo: jbe-solucoes"
          />
          {editingIntegrador ? (
            <Alert severity="info">
              Plano, status e vigência da assinatura são gerenciados em Planos e módulos → Assinaturas.
            </Alert>
          ) : (
            <>
              {catalogLoading && <CircularProgress size={24} />}
              {catalogError && <Alert severity="error">{catalogError}</Alert>}
              {!catalogLoading && catalog && !publishedPlans.length && (
                <Alert severity="warning">
                  Publique um plano em Planos e módulos antes de criar um integrador. Novos cadastros exigem
                  uma assinatura ativa ou trial habilitado.
                </Alert>
              )}
              <FormControl fullWidth disabled={catalogLoading || !publishedPlans.length}>
                <InputLabel>Plano da assinatura</InputLabel>
                <Select
                  label="Plano da assinatura"
                  value={planCode}
                  onChange={(event) => {
                    setPlanCode(event.target.value);
                    setInitialStatus('active');
                  }}
                >
                  {publishedPlans.map((plan) => (
                    <MenuItem key={plan.code} value={plan.code}>
                      {plan.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!planCode}>
                <InputLabel>Ativação inicial</InputLabel>
                <Select
                  label="Ativação inicial"
                  value={initialStatus}
                  onChange={(event) => setInitialStatus(event.target.value as 'active' | 'trial')}
                >
                  <MenuItem value="active">Assinatura ativa</MenuItem>
                  {trialAvailable && planCode === catalog?.settings?.trialPlan && (
                    <MenuItem value="trial">Trial de {catalog?.settings?.trialDays} dias</MenuItem>
                  )}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Os módulos e limites do plano serão aplicados antes de liberar a nova aplicação. A vigência da
                assinatura ativa pode ser ajustada em Assinaturas.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={submitting} onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              submitting ||
              !formData.nome ||
              !formData.email ||
              !formData.empresa ||
              !formData.alias ||
              (!editingIntegrador && !subscriptionReady)
            }
          >
            {submitting ? <CircularProgress size={24} /> : editingIntegrador ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão de instância</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px' }}>
          {deleteTarget && (
            <>
              {isProtectedTarget ? (
                <Alert severity="error">
                  Esta instância é protegida por política de segurança e não pode ser excluída.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Esta ação é irreversível e remove permanentemente os recursos da instância.
                </Alert>
              )}

              <Box sx={{ backgroundColor: '#fafafa', border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                <Typography variant="body2">
                  <strong>Integrador:</strong> {deleteTarget.nome}
                </Typography>
                <Typography variant="body2">
                  <strong>Empresa:</strong> {deleteTarget.empresa}
                </Typography>
                <Typography variant="body2">
                  <strong>Alias:</strong> {deleteTarget.alias}
                </Typography>
                <Typography variant="body2">
                  <strong>Domínio:</strong> {deleteTarget.alias}.{TENANT_DOMAIN}
                </Typography>
                <Typography variant="body2">
                  <strong>Namespace:</strong> {deleteTarget.namespace_k8s || `solar-${deleteTarget.alias}`}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Impacto da exclusão: namespace Kubernetes, banco MongoDB da instância, bucket S3 de mídia,
                configuração Evolution/WhatsApp e registro no Management.
              </Typography>

              {!isProtectedTarget && (
                <TextField
                  fullWidth
                  label={`Digite \"${deleteTarget.alias}\" para confirmar`}
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
                      e.preventDefault();
                    }
                  }}
                  autoComplete="off"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={!canConfirmDelete || deleting}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Excluir definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
